import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { useApiClient } from "./api-context.js";
import { authQueryCacheScope } from "./auth-query-cache.js";
import { PageLoadError } from "../components/PageLoadError.js";
import {
  applyRemoteBrowserAccountChange,
  browserAccountChangeBlocksRender,
  browserAccountEventHandled,
  browserAccountTabId,
  clearTenantBrowserTransientState,
  createBrowserAccountChangeEvent,
  markBrowserAccountEventHandled,
  publishBrowserAccountChange,
  readRecentBrowserAccountChange,
  subscribeBrowserAccountChanges,
  type BrowserAccountChangeEvent,
} from "./account-browser-state.js";

export interface AccountChangeInput<T> {
  path: "/account/switch" | "/account/invitations/accept" | "/integrations/ecomos/handoff";
  body: unknown;
  targetAccountId?: string | null;
  destination: string;
  /** Prevent an auto-running transition route from reloading into a retry loop. */
  failureDestination?: string;
  afterRebind?: (result: T) => Promise<void>;
}

type AccountChange = <T>(input: AccountChangeInput<T>) => Promise<T>;

interface AccountResponse {
  account: { id: string };
}

const AccountContext = createContext<string | null>(null);
const AccountChangeContext = createContext<AccountChange | null>(null);
const ACCOUNT_SCOPE_QUERY_KEY = ["browser-account-scope"] as const;

function accountHandshakeErrorCode(error: unknown): unknown {
  const detail = error && typeof error === "object" && "detail" in error
    ? (error as { detail?: unknown }).detail
    : null;
  return detail && typeof detail === "object" && "code" in detail
    ? (detail as { code?: unknown }).code
    : null;
}

export function accountHandshakeErrorContent(error: unknown): { title: string; body?: string } {
  const code = accountHandshakeErrorCode(error);
  if (code === "identity_maintenance") {
    return {
      title: "Login update in progress",
      body: "Try again in a minute.",
    };
  }
  if (code === "invitation_provider_unavailable") {
    return {
      title: "Finishing your account setup",
      body: "This usually takes a few seconds. Try again.",
    };
  }
  return { title: "We couldn't load your account" };
}

export function accountHandshakeShouldRetry(failureCount: number, error: unknown): boolean {
  return accountHandshakeErrorCode(error) === "invitation_provider_unavailable"
    && failureCount < 3;
}

export function accountHandshakeRedirect(error: unknown): string | null {
  const detail = error && typeof error === "object" && "detail" in error
    ? (error as { detail?: unknown }).detail
    : null;
  const code = detail && typeof detail === "object" && "code" in detail
    ? (detail as { code?: unknown }).code
    : null;
  // Password-gated invitation identities must return through their original
  // private app link. Never expose the legacy finish form after an ordinary
  // Clerk sign-in; it could change the identity outside the bound setup flow.
  return code === "password_setup_required" ? "/create-account" : null;
}

export function AccountContextBoundary({
  children,
  requireBinding = true,
}: {
  children: React.ReactNode;
  requireBinding?: boolean;
}): React.ReactElement | null {
  const { isLoaded, isSignedIn, userId, sessionId } = useAuth();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const scope = authQueryCacheScope({ isLoaded, isSignedIn, userId, sessionId });
  const signedInScope = scope?.startsWith("signed-in:") ? scope : null;
  const tabId = useMemo(() => browserAccountTabId(), []);
  // A tab opened during another tab's PREPARE/COMMIT must never render once
  // before the subscription effect gets a chance to process the saved event.
  const [blocked, setBlocked] = useState(() => {
    if (!signedInScope) return false;
    const recent = readRecentBrowserAccountChange(signedInScope);
    return recent !== null && browserAccountChangeBlocksRender(
      recent,
      signedInScope,
      tabId,
      browserAccountEventHandled(signedInScope, recent.eventId),
    );
  });
  const [readyAccountId, setReadyAccountId] = useState<string | null>(null);
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const accountQ = useQuery({
    queryKey: ["account", "context-handshake"],
    queryFn: () => api.bindAccountContext<AccountResponse>(),
    enabled: requireBinding && signedInScope !== null && !blocked,
    staleTime: 0,
    gcTime: 0,
    retry: accountHandshakeShouldRetry,
    retryDelay: 1_000,
  });
  const currentAccountId = accountQ.data?.account.id ?? null;
  const currentAccountIdRef = useRef<string | null>(null);
  currentAccountIdRef.current = currentAccountId;

  const reloadNow = useCallback((): void => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    window.location.reload();
  }, []);

  const handleRemoteEvent = useCallback((event: BrowserAccountChangeEvent): void => {
    if (!signedInScope) return;
    const handled = browserAccountEventHandled(signedInScope, event.eventId);
    const applied = applyRemoteBrowserAccountChange({
      event,
      authScope: signedInScope,
      tabId,
      handled,
      effects: {
        blockRender: () => setBlocked(true),
        freezeRequests: () => api.freezeRequests(),
        clearQueryCache: () => queryClient.clear(),
        clearTransientState: clearTenantBrowserTransientState,
        reload: reloadNow,
        scheduleFallbackReload: () => {
          if (reloadTimer.current) clearTimeout(reloadTimer.current);
          reloadTimer.current = setTimeout(reloadNow, 10_000);
        },
      },
    });
    if (applied) markBrowserAccountEventHandled(signedInScope, event.eventId);
  }, [api, queryClient, reloadNow, signedInScope, tabId]);

  useEffect(() => {
    if (!signedInScope) return undefined;
    const unsubscribe = subscribeBrowserAccountChanges(signedInScope, handleRemoteEvent);
    const recent = readRecentBrowserAccountChange(signedInScope);
    if (recent) handleRemoteEvent(recent);
    return () => {
      unsubscribe();
      if (reloadTimer.current) clearTimeout(reloadTimer.current);
    };
  }, [handleRemoteEvent, signedInScope]);

  useEffect(() => {
    if (!currentAccountId || blocked) return;
    const previous = queryClient.getQueryData<string>(ACCOUNT_SCOPE_QUERY_KEY) ?? null;
    if (previous !== null && previous !== currentAccountId) {
      api.freezeRequests();
      queryClient.clear();
      clearTenantBrowserTransientState();
      window.location.reload();
      return;
    }
    queryClient.setQueryData(ACCOUNT_SCOPE_QUERY_KEY, currentAccountId);
    setReadyAccountId(currentAccountId);
  }, [api, blocked, currentAccountId, queryClient]);

  const changeAccount = useCallback(async <T,>(input: AccountChangeInput<T>): Promise<T> => {
    if (!signedInScope) throw new Error("Sign in again before switching accounts.");
    const eventId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const start = createBrowserAccountChangeEvent({
      authScope: signedInScope,
      sourceTabId: tabId,
      phase: "start",
      targetAccountId: input.targetAccountId ?? null,
      eventId,
    });
    if (!publishBrowserAccountChange(start)) {
      throw new Error("This browser could not safely switch accounts. Refresh and try again.");
    }

    // PREPARE locally too: unmount old-account UI and erase its request state,
    // while leaving this one ApiClient unfrozen for the committed switch call.
    setBlocked(true);
    queryClient.clear();
    clearTenantBrowserTransientState();
    // Give peer tabs one event-loop turn to process PREPARE and freeze before
    // this tab mutates the shared users.account_id pointer.
    await new Promise<void>((resolve) => window.setTimeout(resolve, 0));

    try {
      const result = await api.postAccountChange<T>(input.path, input.body);
      if (input.afterRebind) await input.afterRebind(result);
      const committedAccountId = api.getExpectedAccountId() ?? input.targetAccountId ?? null;
      const commit = createBrowserAccountChangeEvent({
        authScope: signedInScope,
        sourceTabId: tabId,
        phase: "commit",
        targetAccountId: committedAccountId,
        eventId: `${eventId}:commit`,
      });
      publishBrowserAccountChange(commit);
      // The next document gets a new in-memory tab ID. Mark this event so the
      // source navigation does not mistake its own saved COMMIT for a peer's.
      markBrowserAccountEventHandled(signedInScope, commit.eventId);
      api.freezeRequests();
      window.location.assign(input.destination);
      return result;
    } catch (error) {
      const cancel = createBrowserAccountChangeEvent({
        authScope: signedInScope,
        sourceTabId: tabId,
        phase: "cancel",
        targetAccountId: currentAccountIdRef.current,
        eventId: `${eventId}:cancel`,
      });
      publishBrowserAccountChange(cancel);
      markBrowserAccountEventHandled(signedInScope, cancel.eventId);
      api.freezeRequests();
      if (input.failureDestination) window.location.assign(input.failureDestination);
      else window.location.reload();
      throw error;
    }
  }, [api, queryClient, signedInScope, tabId]);

  if (!isLoaded) return null;
  if (isSignedIn !== true) {
    return <AccountChangeContext.Provider value={changeAccount}>{children}</AccountChangeContext.Provider>;
  }
  if (blocked) return null;
  // Invitation acceptance is the one authenticated mutation that can create
  // the local user/account binding. It therefore gets the same two-phase tab
  // fence, but must not GET /account before the POST can supply its invite token.
  if (!requireBinding) {
    return <AccountChangeContext.Provider value={changeAccount}>{children}</AccountChangeContext.Provider>;
  }
  if (accountQ.isError) {
    const redirect = accountHandshakeRedirect(accountQ.error);
    if (redirect !== null) return <Navigate to={redirect} replace />;
    const content = accountHandshakeErrorContent(accountQ.error);
    return (
      <PageLoadError
        title={content.title}
        {...(content.body ? { body: content.body } : {})}
        retrying={accountQ.isFetching}
        onRetry={() => { void accountQ.refetch(); }}
      />
    );
  }
  if (!currentAccountId || readyAccountId !== currentAccountId) return null;

  return (
    <AccountContext.Provider value={currentAccountId}>
      <AccountChangeContext.Provider value={changeAccount}>
        {children}
      </AccountChangeContext.Provider>
    </AccountContext.Provider>
  );
}

export function useActiveAccountId(): string | null {
  return useContext(AccountContext);
}

export function useRequiredActiveAccountId(): string {
  const accountId = useActiveAccountId();
  if (!accountId) throw new Error("Active account context is not ready");
  return accountId;
}

export function useAccountChange(): AccountChange {
  const context = useContext(AccountChangeContext);
  if (!context) throw new Error("useAccountChange must be used inside AccountContextBoundary");
  return context;
}
